// The "change the monitored security" flow, as a section embedded in the Market Target
// page. Two steps: preview (shows the new security's snapshot and a required warning),
// then a confirmed activation that may require an Account Reference Value. It operates on
// the target already loaded by the parent and calls onChanged() after a confirmed change
// so the parent re-reads the target, state, and ladder (a change starts a new drawdown
// episode, which the ladder/headroom display must reflect).

import { useEffect, useState } from "react";

import { useApi } from "../api/useApi";
import type { ChangePreview, MarketTarget } from "../types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const SELECT_CLASS =
  "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

export function ChangeSecuritySection({
  target,
  onChanged,
  expanded: controlledExpanded,
  onExpandedChange,
  showTrigger = true,
}: {
  target: MarketTarget;
  onChanged: () => void | Promise<void>;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  showTrigger?: boolean;
}) {
  const api = useApi();
  const [symbol, setSymbol] = useState<string>(target.symbol);
  const [priceBasis, setPriceBasis] = useState<string>(target.price_basis);
  const [preview, setPreview] = useState<ChangePreview | null>(null);
  const [arv, setArv] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState("");
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded ?? internalExpanded;

  const setExpanded = (value: boolean) => {
    if (controlledExpanded === undefined) setInternalExpanded(value);
    onExpandedChange?.(value);
  };

  useEffect(() => {
    setSymbol(target.symbol);
    setPriceBasis(target.price_basis);
    if (!expanded) {
      setPreview(null);
      setReason("");
      setArv("");
      setStatus("");
    }
  }, [expanded, target.symbol, target.price_basis]);

  const requestPreview = async () => {
    setStatus("");
    try {
      const resp = (await api.changeTarget(target.market_target_id, {
        symbol,
        price_basis: priceBasis,
      })) as ChangePreview;
      setPreview(resp);
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Preview failed");
    }
  };

  const confirm = async () => {
    setStatus("");
    try {
      await api.changeTarget(target.market_target_id, {
        symbol,
        price_basis: priceBasis,
        confirm: true,
        account_reference_value: preview?.requires_account_reference_value
          ? Number(arv)
          : undefined,
        reason: reason || undefined,
      });
      setStatus("Change activated. A new drawdown episode has started.");
      setPreview(null);
      setReason("");
      setArv("");
      setExpanded(false);
      await onChanged();
    } catch (err) {
      setStatus(err instanceof Error ? err.message : "Change failed");
    }
  };

  if (!showTrigger && !expanded) return null;

  return (
    <div
      className={showTrigger ? "border-t border-border pt-4" : undefined}
      data-testid="change-security"
    >
      {showTrigger && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium">Monitored security</p>
            <p className="text-xs text-muted-foreground">
              Change the symbol or price basis.
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            aria-expanded={expanded}
            onClick={() => {
              if (expanded) {
                setSymbol(target.symbol);
                setPriceBasis(target.price_basis);
                setReason("");
                setArv("");
              }
              setExpanded(!expanded);
              setPreview(null);
              setStatus("");
            }}
          >
            {expanded ? "Cancel" : "Change"}
          </Button>
        </div>
      )}

      {expanded && (
        <div
          className={`${showTrigger ? "mt-4 " : ""}space-y-4 rounded-md border border-border/70 p-4`}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="change-symbol">Symbol</Label>
              <Input
                id="change-symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                aria-label="symbol"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="change-price-basis">Price basis</Label>
              <select
                id="change-price-basis"
                className={SELECT_CLASS}
                value={priceBasis}
                onChange={(e) => setPriceBasis(e.target.value)}
                aria-label="price-basis"
              >
                <option value="unadjusted_close">Unadjusted closing price</option>
                <option value="split_adjusted_close">Split-adjusted closing price</option>
                <option value="total_return_close">Total-return closing price</option>
              </select>
            </div>
          </div>
          <Button type="button" onClick={requestPreview}>
            Preview change
          </Button>

          {preview && (
            <div
              className="change-preview space-y-4 rounded-md border border-warning/50 p-4"
              data-testid="change-preview"
            >
            <Alert data-testid="change-warning" className="change-warning">
              <AlertTitle>Heads up</AlertTitle>
              <AlertDescription>{preview.warning}</AlertDescription>
            </Alert>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">Symbol</dt>
                <dd className="text-sm font-medium">{preview.preview.symbol}</dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Latest close
                </dt>
                <dd className="text-sm font-medium tabular-nums">
                  {preview.preview.latest_close}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  All-time high
                </dt>
                <dd className="text-sm font-medium tabular-nums">
                  {preview.preview.all_time_high}
                </dd>
              </div>
              <div className="flex flex-col gap-0.5">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                  Current drawdown
                </dt>
                <dd className="text-sm font-medium tabular-nums">
                  {preview.preview.current_drawdown_percent.toFixed(2)}%
                </dd>
              </div>
            </dl>

            {preview.requires_account_reference_value && (
              <div className="space-y-2">
                <Label htmlFor="account-reference-value">
                  Confirm Account Reference Value
                </Label>
                <Input
                  id="account-reference-value"
                  type="number"
                  value={arv}
                  onChange={(e) => setArv(e.target.value)}
                  aria-label="account-reference-value"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="change-reason">Reason (optional)</Label>
              <Input
                id="change-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                aria-label="reason"
              />
            </div>

            <Button
              type="button"
              onClick={confirm}
              disabled={preview.requires_account_reference_value && !arv}
              data-testid="confirm-change"
            >
              Confirm change
            </Button>
            </div>
          )}

          {status && <p className="status text-sm text-muted-foreground">{status}</p>}
        </div>
      )}
      {!expanded && status && (
        <p className="status mt-2 text-sm text-muted-foreground">{status}</p>
      )}
    </div>
  );
}
